import React, { useState, useMemo, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconTrash, IconX, IconChevronLeft, IconChevronRight } from './icons';
import { thumbProps } from '../lib/imageUtils';
import { useConfirm } from '../hooks/useConfirm';
import { Button } from './ui';

const PHOTO_SOURCES = [
    { value: 'office', label: 'Office Upload' },
    { value: 'client', label: 'Client Submitted' },
    { value: 'estimator', label: 'Estimator Walkthrough' },
    { value: 'crew', label: 'Crew / Move Day' },
];

// Read room tags off a photo regardless of which column shape is set.
// New rows write to room_tags (text[]); legacy rows have a single string in
// room_tag. Returns a fresh array.
const photoTags = (p) => {
    if (Array.isArray(p?.room_tags) && p.room_tags.length) return p.room_tags;
    if (p?.room_tag) return [p.room_tag];
    return [];
};

// Velocity threshold for swipe-to-navigate (px/ms). Per Emil's gesture rules:
// "If velocity exceeds ~0.11, dismiss/transition regardless of distance."
// A quick flick should be enough; we don't require dragging halfway across
// the screen.
const SWIPE_VELOCITY = 0.11;
const SWIPE_DISTANCE = 60; // fallback when the gesture is slow but long

// Gallery order inside a batch (sql/089, 2026-09-11): sort_order asc,
// uploaded_at tiebreak. Also fixes the office uploader PREPENDING each
// finished photo, which reversed a fresh batch on screen.
const bySortOrder = (a, b) => {
    const sa = a.sort_order ?? Number.MAX_SAFE_INTEGER;
    const sb = b.sort_order ?? Number.MAX_SAFE_INTEGER;
    if (sa !== sb) return sa - sb;
    return (a.uploaded_at || '').localeCompare(b.uploaded_at || '');
};

export default function PhotoGallery({ photos, onDelete, onReorder }) {
    // In-app confirm (QA M2 NEW-9 native-dialog sweep).
    const { confirm: confirmDialog, confirmElement } = useConfirm();
    const [lightbox, setLightbox] = useState(null);
    const [filter, setFilter] = useState('all');
    const [collapsedBatches, setCollapsedBatches] = useState({});

    const filtered = filter === 'all' ? photos : photos.filter(p => photoTags(p).includes(filter) || p.source === filter);
    const roomsUsed = [...new Set(photos.flatMap(photoTags))];

    const batches = useMemo(() => {
        const groups = {};
        filtered.forEach(photo => {
            const key = photo.upload_batch_id || `legacy_${(photo.uploaded_at || photo.created_at || '').slice(0, 10) || 'unknown'}`;
            if (!groups[key]) groups[key] = { id: key, photos: [], label: '', earliest: null };
            groups[key].photos.push(photo);
            if (photo.batch_label && !groups[key].label) groups[key].label = photo.batch_label;
            const ts = photo.uploaded_at || photo.created_at;
            if (ts && (!groups[key].earliest || ts < groups[key].earliest)) groups[key].earliest = ts;
        });
        const list = Object.values(groups).sort((a, b) => (b.earliest || '').localeCompare(a.earliest || ''));
        list.forEach(g => g.photos.sort(bySortOrder));
        return list;
    }, [filtered]);

    const toggleBatch = (batchId) => setCollapsedBatches(prev => ({ ...prev, [batchId]: !prev[batchId] }));

    // ── Reorder mode (Jaylen 2026-09-11) ────────────────────────────────
    // One batch at a time: its header's Reorder button turns every tile
    // into ◀ ▶ arrows (works with a mouse or a finger — no HTML5 drag,
    // which is unreliable on touch and inside our transformed Modal).
    // Done hands the parent the WHOLE gallery in display order (batches
    // newest-first, photos in their new order) so it can renumber 1..n
    // via db.reorderPhotos. Only offered when the parent passes onReorder
    // and no filter is active (a filtered view hides photos the renumber
    // would need).
    const [reorderBatch, setReorderBatch] = useState(null);   // batch id
    const [reorderList, setReorderList] = useState([]);       // working copy of that batch's photos
    const [savingOrder, setSavingOrder] = useState(false);
    const canReorder = typeof onReorder === 'function' && filter === 'all';
    const startReorder = (batch) => { setReorderBatch(batch.id); setReorderList(batch.photos); };
    const cancelReorder = () => { setReorderBatch(null); setReorderList([]); };
    const moveInBatch = (idx, dir) => setReorderList(prev => {
        const j = idx + dir;
        if (j < 0 || j >= prev.length) return prev;
        const next = [...prev];
        [next[idx], next[j]] = [next[j], next[idx]];
        return next;
    });
    const finishReorder = async () => {
        if (!reorderBatch) return;
        setSavingOrder(true);
        try {
            const ordered = batches.flatMap(b => (b.id === reorderBatch ? reorderList : b.photos));
            await onReorder(ordered);
            cancelReorder();
        } finally { setSavingOrder(false); }
    };

    const formatBatchDate = (ts) => {
        if (!ts) return 'Unknown date';
        const d = new Date(ts);
        return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) + ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    };

    const allVisible = batches.flatMap(b => collapsedBatches[b.id] ? [] : b.photos);
    const lightboxIndex = lightbox ? allVisible.findIndex(p => p.id === lightbox.id) : -1;

    const swapTo = (next) => {
        if (!next) return;
        setLightbox(next);
    };

    const goPrev = (e) => {
        if (e) e.stopPropagation();
        if (lightboxIndex <= 0 || allVisible.length === 0) return;
        swapTo(allVisible[lightboxIndex - 1]);
    };
    const goNext = (e) => {
        if (e) e.stopPropagation();
        if (lightboxIndex < 0 || lightboxIndex >= allVisible.length - 1) return;
        swapTo(allVisible[lightboxIndex + 1]);
    };

    // Keyboard nav.
    useEffect(() => {
        if (!lightbox) return;
        const handler = (e) => {
            if (e.key === 'ArrowLeft') goPrev();
            else if (e.key === 'ArrowRight') goNext();
            else if (e.key === 'Escape') setLightbox(null);
        };
        window.addEventListener('keydown', handler);
        return () => window.removeEventListener('keydown', handler);
    }, [lightbox, lightboxIndex, allVisible]);

    // ─── Touch gestures ──────────────────────────────────────────────────
    const touchRef = useRef({ x: 0, y: 0, t: 0 });
    const onTouchStart = (e) => {
        const t = e.touches[0];
        touchRef.current = { x: t.clientX, y: t.clientY, t: Date.now() };
    };
    const onTouchEnd = (e) => {
        const t = e.changedTouches[0];
        const dx = t.clientX - touchRef.current.x;
        const dy = t.clientY - touchRef.current.y;
        const elapsed = Date.now() - touchRef.current.t;
        // Ignore if the user was scrolling vertically, not swiping horizontally.
        if (Math.abs(dy) > Math.abs(dx)) return;
        const velocity = Math.abs(dx) / Math.max(elapsed, 1);
        const isFlick = velocity > SWIPE_VELOCITY;
        const isLongDrag = Math.abs(dx) > SWIPE_DISTANCE;
        if (!isFlick && !isLongDrag) return;
        // Right-swipe → previous, left-swipe → next (matches phone gallery convention).
        if (dx > 0) goPrev();
        else goNext();
    };

    if (photos.length === 0) return <p className="text-sm text-slate-400 py-4 text-center">No photos yet. Upload images above to build the visual inventory.</p>;

    return (
        <div>
            {(roomsUsed.length > 0 || photos.length > 3) && (
                <div className="flex items-center gap-2 mb-4 flex-wrap">
                    <button onClick={() => setFilter('all')} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === 'all' ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All ({photos.length})</button>
                    {roomsUsed.map(room => (
                        <button key={room} onClick={() => setFilter(room)} className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${filter === room ? 'bg-red-600 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>{room} ({photos.filter(p => photoTags(p).includes(room)).length})</button>
                    ))}
                </div>
            )}

            <div className="space-y-4">
                {batches.map(batch => {
                    const isCollapsed = collapsedBatches[batch.id];
                    const batchTitle = batch.label || formatBatchDate(batch.earliest);
                    const sourceLabel = batch.photos[0]?.source ? (PHOTO_SOURCES.find(s => s.value === batch.photos[0].source) || {}).label : '';
                    return (
                        <div key={batch.id} className="border border-slate-200 rounded-xl overflow-hidden">
                            <div
                                className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors select-none"
                                onClick={() => toggleBatch(batch.id)}
                            >
                                <div className="flex items-center gap-3">
                                    <span className={`text-slate-400 transition-transform text-sm ${isCollapsed ? '' : 'rotate-180'}`}>▾</span>
                                    <div>
                                        <span className="text-sm font-semibold text-slate-800">{batchTitle}</span>
                                        {batch.label && <span className="text-xs text-slate-400 ml-2">{formatBatchDate(batch.earliest)}</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
                                    {canReorder && !isCollapsed && batch.photos.length > 1 && (
                                        reorderBatch === batch.id ? (
                                            <>
                                                <Button variant="tertiary" size="sm" onClick={cancelReorder} disabled={savingOrder}>Cancel</Button>
                                                <Button variant="primary" size="sm" onClick={finishReorder} loading={savingOrder}>Done</Button>
                                            </>
                                        ) : (
                                            <Button variant="secondary" size="sm" onClick={() => startReorder(batch)} disabled={!!reorderBatch}>Reorder</Button>
                                        )
                                    )}
                                    {sourceLabel && <span className="text-xs text-slate-500 bg-white px-2 py-0.5 rounded-full border border-slate-200">{sourceLabel}</span>}
                                    <span className="bg-slate-200 text-slate-600 text-xs px-2 py-0.5 rounded-full font-medium">{batch.photos.length} photo{batch.photos.length !== 1 ? 's' : ''}</span>
                                </div>
                            </div>
                            {!isCollapsed && reorderBatch === batch.id && (
                                <div className="grid grid-cols-4 gap-3 p-3 bg-indigo-50/40">
                                    {reorderList.map((photo, idx) => (
                                        <div key={photo.id} className="relative rounded-lg overflow-hidden border-2 border-indigo-300 aspect-square bg-slate-100">
                                            <img {...thumbProps(photo.url, { width: 400, height: 400 })} alt={photo.label || ''} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            <span className="absolute top-1.5 left-1.5 bg-black/60 text-white text-[11px] font-bold px-1.5 py-0.5 rounded">{idx + 1}</span>
                                            <div className="absolute inset-x-0 bottom-0 flex">
                                                <button type="button" onClick={() => moveInBatch(idx, -1)} disabled={idx === 0} className="flex-1 h-10 bg-black/55 hover:bg-black/75 text-white font-bold flex items-center justify-center disabled:opacity-30" aria-label="Move earlier">◀</button>
                                                <button type="button" onClick={() => moveInBatch(idx, 1)} disabled={idx === reorderList.length - 1} className="flex-1 h-10 bg-black/55 hover:bg-black/75 text-white font-bold flex items-center justify-center border-l border-white/20 disabled:opacity-30" aria-label="Move later">▶</button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {!isCollapsed && reorderBatch !== batch.id && (
                                <div className="grid grid-cols-4 gap-3 p-3">
                                    {batch.photos.map(photo => (
                                        <div key={photo.id} className="group relative rounded-lg overflow-hidden border border-slate-200 hover:border-red-300 cursor-pointer aspect-square bg-slate-100" style={{ contentVisibility: 'auto', containIntrinsicSize: '200px 200px' }} onClick={() => swapTo(photo)}>
                                            <img {...thumbProps(photo.url, { width: 400, height: 400 })} alt={photo.label || ''} className="w-full h-full object-cover" loading="lazy" decoding="async" />
                                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                                                <div className="absolute bottom-0 left-0 right-0 p-2 flex flex-wrap gap-1">
                                                    {photoTags(photo).map(t => (
                                                        <span key={t} className="px-1.5 py-0.5 bg-white/90 text-slate-700 text-xs rounded font-medium">{t}</span>
                                                    ))}
                                                    <span className="px-1.5 py-0.5 bg-white/90 text-slate-500 text-xs rounded">{(PHOTO_SOURCES.find(s => s.value === photo.source) || {}).label || photo.source}</span>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Portal the lightbox to <body> so it escapes any transformed
                ancestor (e.g. our Modal animates with `transform: scale(1)`,
                which would otherwise turn this `position: fixed` into a
                fixed-to-the-modal positioning — causing the lightbox to
                render at the top of the modal's scroll area instead of the
                viewport, and the prev/next buttons to land outside the
                visible lightbox bounds). */}
            {lightbox && createPortal((
                <div
                    className="fixed inset-0 z-[200] bg-black/90 flex items-center justify-center"
                    // Only close when the click is on the backdrop ITSELF.
                    // Descendant clicks that bubble (or stray edge clicks
                    // near the chevron buttons) no longer accidentally
                    // dismiss the lightbox.
                    onClick={(e) => { if (e.target === e.currentTarget) setLightbox(null); }}
                    onTouchStart={onTouchStart}
                    onTouchEnd={onTouchEnd}
                >
                    {/* Chevron buttons are ALWAYS rendered (disabled at
                        boundaries, not unmounted) so the hit-target stays
                        at the same screen position — users develop muscle
                        memory and clicks never "fall through" to the
                        backdrop during a re-render frame.

                        Hit zone strategy: the BUTTON is a tall full-height
                        strip that fills the entire side gap (up to ~25% of
                        viewport on big screens, w-32 floor on mobile). The
                        visible chevron pill is just a styled <span> inside,
                        anchored to the outer edge. This means clicking
                        anywhere in that whole strip navigates — you don't
                        have to land on the 60px pill. */}
                    <button
                        onClick={(e) => { e.stopPropagation(); goPrev(e); }}
                        disabled={lightboxIndex <= 0}
                        className="group absolute left-0 top-0 bottom-0 w-32 sm:w-1/5 sm:max-w-[220px] z-10 flex items-center justify-start pl-3 sm:pl-4 disabled:cursor-not-allowed"
                        aria-label="Previous photo"
                    >
                        <span className="bg-white/90 group-hover:bg-white group-disabled:opacity-30 text-slate-700 p-4 min-w-[60px] min-h-[60px] flex items-center justify-center rounded-full shadow-lg transition-colors">
                            <IconChevronLeft size={28}/>
                        </span>
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); goNext(e); }}
                        disabled={lightboxIndex < 0 || lightboxIndex >= allVisible.length - 1}
                        className="group absolute right-0 top-0 bottom-0 w-32 sm:w-1/5 sm:max-w-[220px] z-10 flex items-center justify-end pr-3 sm:pr-4 disabled:cursor-not-allowed"
                        aria-label="Next photo"
                    >
                        <span className="bg-white/90 group-hover:bg-white group-disabled:opacity-30 text-slate-700 p-4 min-w-[60px] min-h-[60px] flex items-center justify-center rounded-full shadow-lg transition-colors">
                            <IconChevronRight size={28}/>
                        </span>
                    </button>
                    {/* Sizing (Jaylen 2026-07-08 screen-mirroring note):
                        photos are reviewed together on a big monitor/TV,
                        where the old max-w-4xl (896px) cap left them small
                        in the middle of the screen. Now the image fills
                        the viewport minus the chevron side strips (the
                        200px reservation keeps the pills clickable and
                        the container above them un-overlapped on sm+;
                        phones keep the 95vw behavior). */}
                    <div className="relative max-w-[95vw] sm:max-w-[calc(100vw-200px)] max-h-[92vh] mx-4" onClick={e => e.stopPropagation()}>
                        <img
                            src={lightbox.url}
                            alt={lightbox.label || ''}
                            className="max-w-full max-h-[85vh] object-contain rounded-lg"
                        />
                        {/* z-30 lifts the close/delete cluster above the
                            z-10 chevron side strips — with the wider
                            image the top-right corner can sit inside the
                            right strip's hit zone. */}
                        <div className="absolute top-2 right-2 flex gap-2 z-30">
                            {onDelete && <button className="bg-red-600 text-white p-2 rounded-full hover:bg-red-700 shadow-lg" onClick={async () => { if (await confirmDialog({ title: `Delete ${lightbox.label ? `"${lightbox.label}"` : (lightbox.batch_label ? `this ${lightbox.batch_label} photo` : 'this photo')}?`, body: 'The photo is removed from the client file. The rest of the batch and the client record are kept. This can\'t be undone.', confirmLabel: 'Delete photo', danger: true })) { await onDelete(lightbox); setLightbox(null); } }}><IconTrash size={16}/></button>}
                            <button className="bg-white/90 text-slate-700 p-2 rounded-full hover:bg-white shadow-lg" onClick={() => setLightbox(null)}><IconX size={16}/></button>
                        </div>
                        <div className="mt-3 flex items-center gap-3 text-white text-sm">
                            {lightbox.batch_label && <span className="bg-white/20 px-2 py-1 rounded font-medium">{lightbox.batch_label}</span>}
                            {photoTags(lightbox).map(t => (
                                <span key={t} className="bg-white/20 px-2 py-1 rounded">{t}</span>
                            ))}
                            <span className="bg-white/20 px-2 py-1 rounded">{(PHOTO_SOURCES.find(s => s.value === lightbox.source) || {}).label}</span>
                            <span className="text-white/60 text-xs">{lightbox.file_size ? `${(lightbox.file_size / 1024).toFixed(0)} KB` : ''}</span>
                            {allVisible.length > 1 && <span className="text-white/60 text-xs ml-auto">{lightboxIndex + 1} / {allVisible.length}</span>}
                            {lightbox.notes && <span className="text-white/80">{lightbox.notes}</span>}
                        </div>
                    </div>
                </div>
            ), document.body)}
            {confirmElement}
        </div>
    );
}
